import { nhost } from "@/lib/nhost";

const PUSH_PUBLIC_KEY = "BG8fGXk0MdUzqfdEr7Np_yL1m0giMH_g_NJoDU6mm8wzr9YDClPzXg3_6ioXGQs2eJhFXMI1pgrsbAodoAIFndA";

export type PushReason = "saved" | "ios-home-screen" | "denied" | "unsupported" | "server";

function keyToBytes(s: string): Uint8Array {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export function isIos(): boolean {
  return typeof navigator !== "undefined" && /iPhone|iPad|iPod/.test(navigator.userAgent);
}

function isInstalledApp(): boolean {
  const standalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return standalone || (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches);
}

export function pushSupported(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

async function save(sub: PushSubscription, orderReference?: string): Promise<boolean> {
  try {
    const res = await nhost.functions.post("/push-subscribe", {
      site: "main",
      subscription: sub.toJSON(),
      tags: ["visitor"],
      order_reference: orderReference || undefined,
    });
    return !!(res.body as { success?: boolean } | null)?.success;
  } catch {
    return false;
  }
}

// Called when the person taps "Turn on"
export async function enablePush(orderReference?: string): Promise<{ ok: boolean; reason: PushReason }> {
  if (isIos() && !isInstalledApp()) return { ok: false, reason: "ios-home-screen" };
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: "denied" };
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyToBytes(PUSH_PUBLIC_KEY) as unknown as BufferSource,
    });
  }
  const ok = await save(sub, orderReference);
  return { ok, reason: ok ? "saved" : "server" };
}

// Quietly refreshes tags for someone who already allowed alerts (for example after they log in)
export async function syncPush(orderReference?: string): Promise<void> {
  if (!pushSupported() || Notification.permission !== "granted") return;
  const reg = await navigator.serviceWorker.getRegistration("/");
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) await save(sub, orderReference);
}
